import client, { routes } from '@/utils/axios';
import { ACCESS_TOKEN, DEMO_MODE, REFRESH_TOKEN } from '@/utils/utils';

/**
 * Mode démo : le site vitrine ouvre la web app dans une iframe avec
 * `?demo=1&grant=<laissez-passer>`.
 *
 * Le laissez-passer est signé par le serveur du site et vaut deux minutes.
 * C'est lui qui réserve la démonstration au site : sans lui, `POST /auth/demo`
 * refuse, et ouvrir l'application directement ne donne plus rien.
 *
 * La lecture seule est garantie côté serveur (DemoReadOnlyGuard) ; ici on
 * empêche en plus l'interface d'émettre la moindre écriture.
 */

function demoGrant(): string | null {
  if (typeof window === 'undefined' || IS_DESKTOP) return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('demo') !== '1') return null;
  return params.get('grant');
}

import { IS_DESKTOP } from '@/config/platform';
export function isDemoMode() {
  return window.store.get(DEMO_MODE) === 'true';
}

/** Sortie de démo : à appeler à toute connexion réelle. */
export function clearDemoMode() {
  window.store.set(DEMO_MODE, 'false');
}

/**
 * À appeler avant le rendu. Sans laissez-passer, ne fait rien.
 *
 * Avec laissez-passer, la session de démonstration est ouverte
 * INCONDITIONNELLEMENT : le cadre du site affiche toujours le même compte, quel
 * que soit l'état du navigateur du visiteur. La version précédente s'abstenait
 * dès qu'une session existait, et un visiteur déjà connecté à l'application
 * dans un autre onglet voyait son propre compte s'afficher sur le site.
 *
 * Écraser est sans danger uniquement parce que `web-setup.ts` cloisonne la démo
 * dans `sessionStorage` : la vraie session vit dans un autre espace, elle n'est
 * ni lue ni écrite ici.
 */
export async function bootstrapDemoSession() {
  const grant = demoGrant();
  if (!grant) return;

  try {
    const { data } = await client.post<{
      accessToken: string;
      refreshToken: string;
    }>(routes.auth.demo, { grant });
    window.store.set(ACCESS_TOKEN, data.accessToken);
    window.store.set(REFRESH_TOKEN, data.refreshToken);
    window.store.set(DEMO_MODE, 'true');
  } catch {
    // Laissez-passer refusé ou démonstration non configurée : on laisse le
    // flux normal rediriger vers l'écran de connexion.
    clearDemoMode();
  } finally {
    // Le laissez-passer ne sert qu'une fois : on le retire de la barre
    // d'adresse pour qu'il ne soit ni recopié ni rejoué au rechargement.
    const url = new URL(window.location.href);
    url.searchParams.delete('grant');
    window.history.replaceState(null, '', url.toString());
  }
}
